import React, { useEffect, useRef, useState, useCallback } from "react";
import publicApi from "@/lib/publicApi";
import { Input } from "@/components/ui/input";
import { Loader2, MessageCircle, X, Send, Phone, UserRound, ShieldCheck } from "lucide-react";

const LS_KEY = "mmm_chat";
const telHref = (phone) => `tel:${(phone || "").replace(/[^0-9+]/g, "")}`;

const readStore = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "null"); } catch { return null; }
};
const writeStore = (v) => localStorage.setItem(LS_KEY, JSON.stringify(v));
const clearStore = () => localStorage.removeItem(LS_KEY);

const Bubble = ({ m, visitorName }) => {
  if (m.role === "system") {
    return (
      <div className="flex justify-center my-2" data-testid="chat-message-system">
        <span className="text-[12px] text-[#4B5563] bg-[#1C4358]/6 rounded-full px-3 py-1">{m.text}</span>
      </div>
    );
  }
  const isVisitor = m.role === "visitor";
  return (
    <div className={`flex ${isVisitor ? "justify-end" : "justify-start"} mb-2.5`} data-testid={`chat-message-${m.role}`}>
      <div className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-[15px] leading-relaxed ${
        isVisitor ? "bg-[#9C1E22] text-white rounded-br-md" : "bg-white border border-[#1C4358]/12 text-[#252221] rounded-bl-md"}`}>
        {!isVisitor && m.sender_name && (
          <div className="text-[11px] font-semibold text-[#1C4358]/70 mb-0.5">{m.sender_name}</div>
        )}
        {m.text}
      </div>
    </div>
  );
};

export const ChatWidget = ({ open, onOpenChange, prefill, content }) => {
  const [config, setConfig] = useState(null);
  const [session, setSession] = useState(null);      // {id, token}
  const [messages, setMessages] = useState([]);
  const [seq, setSeq] = useState(0);
  const [status, setStatus] = useState("ai_active");
  const [agentName, setAgentName] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", state: "", relationship: "", relationship_other: "" });
  const [step, setStep] = useState("intro"); // intro (who are you helping) -> form
  const [starting, setStarting] = useState(false);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const phone = content?.phone;

  // load config once
  useEffect(() => {
    publicApi.get("/public/chat/config").then(({ data }) => setConfig(data)).catch(() => setConfig({ enabled: true }));
    const stored = readStore();
    if (stored?.session_id && stored?.token) {
      setSession({ id: stored.session_id, token: stored.token });
    }
  }, []);

  const scrollDown = useCallback(() => {
    requestAnimationFrame(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; });
  }, []);

  // when opened with an existing session, hydrate transcript
  const hydrate = useCallback(async () => {
    if (!session) return;
    try {
      const { data } = await publicApi.get(`/public/chat/${session.id}`, { params: { token: session.token, after: 0 } });
      setMessages(data.messages || []);
      setStatus(data.status);
      setAgentName(data.assigned_agent_name);
      setSeq((data.messages || []).reduce((mx, m) => Math.max(mx, m.seq || 0), 0));
      scrollDown();
    } catch (e) {
      if (e?.response?.status === 403) { clearStore(); setSession(null); }
    }
  }, [session, scrollDown]);

  useEffect(() => { if (open && session && messages.length === 0) hydrate(); }, [open, session, messages.length, hydrate]);

  // when opened via /chat/continue, re-read the stored session
  useEffect(() => {
    if (open && !session) {
      const stored = readStore();
      if (stored?.session_id && stored?.token) setSession({ id: stored.session_id, token: stored.token });
    }
  }, [open, session]);

  // polling for agent/system replies (paused when tab hidden)
  useEffect(() => {
    if (!open || !session) return;
    pollRef.current = setInterval(async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const { data } = await publicApi.get(`/public/chat/${session.id}`, { params: { token: session.token, after: seq } });
        if ((data.messages || []).length) {
          setMessages((prev) => [...prev, ...data.messages]);
          setSeq((prev) => Math.max(prev, ...data.messages.map((m) => m.seq || 0)));
          scrollDown();
        }
        setStatus(data.status);
        setAgentName(data.assigned_agent_name);
      } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [open, session, seq, scrollDown]);

  const start = async (topicKey) => {
    if (!form.name.trim() || !form.phone.trim() || !form.relationship) return;
    setStarting(true);
    try {
      const payload = {
        name: form.name.trim(), phone: form.phone.trim(),
        relationship_to_loved_one: form.relationship,
        relationship_other: form.relationship === "other" ? (form.relationship_other.trim() || null) : null,
        email: form.email.trim() || null, city: form.city.trim() || null, state: form.state.trim() || null,
        page_path: window.location.pathname + window.location.search,
        cta_source: prefill?.cta_source || "Start Live Chat",
        reverse_search_topic: prefill?.reverse_search_topic || null,
        matched_service_lane: prefill?.matched_service_lane || null,
        location_slug: prefill?.location_slug || null,
        franchise: prefill?.franchise || null,
        topic_key: topicKey || prefill?.topic_key || null,
      };
      const { data } = await publicApi.post("/public/chat/start", payload);
      const sess = { id: data.session_id, token: data.token };
      setSession(sess);
      writeStore({ session_id: data.session_id, token: data.token });
      setMessages(data.messages || []);
      setStatus(data.status);
      setSeq((data.messages || []).reduce((mx, m) => Math.max(mx, m.seq || 0), 0));
      scrollDown();
    } catch { /* toast handled below */ }
    finally { setStarting(false); }
  };

  const send = async (text, topicKey) => {
    const body = (text ?? input).trim();
    if (!body || !session) return;
    setInput("");
    setSending(true);
    try {
      const { data } = await publicApi.post(`/public/chat/${session.id}/message`, { text: body, topic_key: topicKey || null }, { params: { token: session.token } });
      setMessages(data.messages || []);
      setStatus(data.status);
      setSeq((data.messages || []).reduce((mx, m) => Math.max(mx, m.seq || 0), 0));
      scrollDown();
    } catch { /* ignore */ }
    finally { setSending(false); }
  };

  const requestHuman = async () => {
    if (!session) return;
    try {
      const { data } = await publicApi.post(`/public/chat/${session.id}/request-human`, {}, { params: { token: session.token } });
      setMessages(data.messages || []);
      setStatus(data.status);
      setSeq((data.messages || []).reduce((mx, m) => Math.max(mx, m.seq || 0), 0));
      scrollDown();
    } catch { /* ignore */ }
  };

  if (config && !config.enabled) return null;
  const started = !!session;
  const laneChosen = messages.some((m) => m.role === "visitor");

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          data-testid="chat-launcher"
          onClick={() => onOpenChange(true)}
          className="mmm-tap fixed bottom-20 lg:bottom-6 right-4 z-[60] hidden lg:inline-flex items-center gap-2 rounded-full bg-[#9C1E22] hover:bg-[#861A1D] text-white pl-4 pr-5 h-14 shadow-xl shadow-black/25 transition-colors"
          aria-label="Open live chat"
        >
          <MessageCircle size={22} /> <span className="font-semibold text-[15px] hidden sm:inline">Chat with us</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed z-[70] bottom-0 right-0 left-0 sm:left-auto sm:bottom-6 sm:right-6 sm:w-[400px] h-[100dvh] sm:h-[620px] sm:max-h-[85vh] flex flex-col bg-[#FBF6EC] sm:rounded-[1.2rem] shadow-2xl border border-[#1C4358]/15 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 bg-[#1C4358] text-white px-4 py-3">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/15"><HeartIcon /></span>
              <div>
                <div className="font-display font-semibold text-[15px] leading-tight">MovingMyMama</div>
                <div className="text-[12px] text-white/80 leading-tight flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#DEE029]" /> {agentName ? agentName : "We're here to help"}
                </div>
              </div>
            </div>
            <button data-testid="chat-close" onClick={() => onOpenChange(false)} className="mmm-tap p-1.5 rounded-lg hover:bg-white/10" aria-label="Close chat"><X size={20} /></button>
          </div>

          {/* Body */}
          {!started ? (
            step === "intro" ? (
              <div className="flex-1 overflow-y-auto p-5" data-testid="chat-intro-step">
                <p className="text-[17px] font-display font-semibold text-[#1C4358]">
                  {config?.opening_message || "Hi, I'm here to help. Are you caring for a parent, spouse, or another loved one — or yourself?"}
                </p>
                <p className="mt-1.5 text-[14px] text-[#4B5563] leading-relaxed">Choose the closest — it helps us guide you gently.</p>
                <div className="mt-4 grid grid-cols-2 gap-2.5" data-testid="chat-relationship-options">
                  {(config?.relationship_options || []).map((o) => (
                    <button
                      key={o.value}
                      data-testid={`chat-relationship-${o.value}`}
                      onClick={() => setForm((f) => ({ ...f, relationship: o.value }))}
                      className={`mmm-tap text-[15px] rounded-xl border px-3 py-3 text-left transition-colors ${
                        form.relationship === o.value
                          ? "border-[#9C1E22] bg-[#9C1E22]/8 text-[#1C4358] font-semibold"
                          : "border-[#1C4358]/20 bg-white text-[#252221] hover:bg-[#1C4358]/5"}`}
                    >{o.label}</button>
                  ))}
                </div>
                {form.relationship === "other" && (
                  <div className="mt-3">
                    <label className="text-[13px] font-semibold text-[#1C4358]">{config?.relationship_other_prompt || "Please tell us who you are helping."}</label>
                    <Input data-testid="chat-relationship-other-input" value={form.relationship_other} onChange={(e) => setForm((f) => ({ ...f, relationship_other: e.target.value }))} placeholder="Who you're helping" className="mt-1 h-12 text-[16px] rounded-xl border-[#1C4358]/20 bg-white" />
                  </div>
                )}
                <button
                  data-testid="chat-intro-continue"
                  disabled={!form.relationship || (form.relationship === "other" && !form.relationship_other.trim())}
                  onClick={() => setStep("form")}
                  className="mmm-tap mt-5 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#9C1E22] hover:bg-[#861A1D] text-white font-semibold text-[16px] transition-colors disabled:opacity-60"
                >Continue <Send size={16} /></button>
                <p className="mt-3 flex items-center gap-1.5 text-[12px] text-[#4B5563]"><ShieldCheck size={13} className="text-[#2F7D5A]" /> Your details stay private. No cost, no obligation.</p>
              </div>
            ) : (
            <div className="flex-1 overflow-y-auto p-5" data-testid="chat-start-form">
              <button data-testid="chat-form-back" onClick={() => setStep("intro")} className="text-[13px] text-[#1C4358] hover:underline mb-2">← Back</button>
              <div className="inline-flex items-center gap-1.5 text-[13px] text-[#1C4358] bg-[#1C4358]/8 rounded-full px-3 py-1 mb-2" data-testid="chat-relationship-pill">
                <UserRound size={13} /> Helping: {form.relationship === "other" ? (form.relationship_other || "Other") : (config?.relationship_options || []).find((o) => o.value === form.relationship)?.label}
              </div>
              <p className="text-[17px] font-display font-semibold text-[#1C4358]">You don't have to figure this out alone.</p>
              <p className="mt-1.5 text-[14px] text-[#4B5563] leading-relaxed">Tell us how to reach you and a caring guide will help you find the next right step — no pressure, no cost.</p>
              <div className="mt-4 space-y-3">
                <div>
                  <label className="text-[13px] font-semibold text-[#1C4358]">Your name <span className="text-[#9C1E22]">*</span></label>
                  <Input data-testid="chat-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="First name" className="mt-1 h-12 text-[16px] rounded-xl border-[#1C4358]/20 bg-white" />
                </div>
                <div>
                  <label className="text-[13px] font-semibold text-[#1C4358]">Phone <span className="text-[#9C1E22]">*</span></label>
                  <Input data-testid="chat-phone" type="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="(816) 555-0100" className="mt-1 h-12 text-[16px] rounded-xl border-[#1C4358]/20 bg-white" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[13px] font-semibold text-[#1C4358]">City</label>
                    <Input data-testid="chat-city" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} placeholder="Optional" className="mt-1 h-11 text-[15px] rounded-xl border-[#1C4358]/20 bg-white" />
                  </div>
                  <div>
                    <label className="text-[13px] font-semibold text-[#1C4358]">State</label>
                    <Input data-testid="chat-state" value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} placeholder="Optional" className="mt-1 h-11 text-[15px] rounded-xl border-[#1C4358]/20 bg-white" />
                  </div>
                </div>
                <div>
                  <label className="text-[13px] font-semibold text-[#1C4358]">Email</label>
                  <Input data-testid="chat-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Optional" className="mt-1 h-11 text-[15px] rounded-xl border-[#1C4358]/20 bg-white" />
                </div>
              </div>
              <button
                data-testid="chat-start-submit"
                disabled={starting || !form.name.trim() || !form.phone.trim() || !form.relationship}
                onClick={() => start()}
                className="mmm-tap mt-4 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#9C1E22] hover:bg-[#861A1D] text-white font-semibold text-[16px] transition-colors disabled:opacity-60"
              >
                {starting ? <Loader2 size={18} className="animate-spin" /> : <MessageCircle size={18} />} Start chatting
              </button>
              <p className="mt-3 flex items-center gap-1.5 text-[12px] text-[#4B5563]"><ShieldCheck size={13} className="text-[#2F7D5A]" /> Your details stay private. No cost, no obligation.</p>
            </div>
            )
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4" data-testid="chat-thread">
                {messages.map((m) => <Bubble key={m.id} m={m} />)}
                {(status === "waiting_human" || status === "offline") && (
                  <div className="flex items-center justify-center gap-2 my-2 text-[12px] text-[#B7791F]" data-testid="chat-waiting">
                    <UserRound size={13} /> {status === "offline" ? "We'll follow up soon" : "Connecting you with a team member…"}
                  </div>
                )}
              </div>

              {/* Suggested topics before first message */}
              {!laneChosen && config?.suggested_topics?.length > 0 && (
                <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="chat-topics">
                  {config.suggested_topics.map((t) => (
                    <button key={t.key} data-testid={`chat-topic-${t.key}`} onClick={() => send(t.label, t.key)}
                      className="mmm-tap text-[13px] rounded-full border border-[#1C4358]/25 bg-white text-[#1C4358] px-3 py-1.5 hover:bg-[#1C4358]/6 transition-colors">{t.label}</button>
                  ))}
                </div>
              )}

              {/* Composer */}
              <div className="border-t border-[#1C4358]/12 bg-white p-3">
                <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2">
                  <textarea
                    data-testid="chat-input"
                    rows={1}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Type your message…"
                    className="flex-1 resize-none max-h-28 text-[16px] rounded-xl border border-[#1C4358]/20 px-3 py-2.5 focus:outline-none focus:ring-0"
                  />
                  <button type="submit" data-testid="chat-send" disabled={sending || !input.trim()}
                    className="mmm-tap shrink-0 inline-flex items-center justify-center h-12 w-12 rounded-full bg-[#9C1E22] hover:bg-[#861A1D] text-white disabled:opacity-60 transition-colors">
                    {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  </button>
                </form>
                <div className="mt-2 flex items-center justify-between">
                  {status !== "human_active" && status !== "waiting_human" && (
                    <button data-testid="chat-request-human" onClick={requestHuman} className="text-[12px] font-semibold text-[#1C4358] hover:underline">Talk to a person</button>
                  )}
                  {phone && (
                    <a href={telHref(phone)} className="ml-auto inline-flex items-center gap-1 text-[12px] font-semibold text-[#9C1E22]"><Phone size={12} /> Call {phone}</a>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

const HeartIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
  </svg>
);
